import { reactive, toRefs } from "vue"

// Shared topbar visibility. The scrollable pages (MenuPage, ContentPage, ...)
// forward their ion-content ionScroll event via handleScroll(); App.vue reads
// `topbarsHidden` to slide the company header / tabs / filters out of view
// while the user scrolls down, and back in as soon as they scroll up.
const state = reactive({
    topbarsHidden: false
})

let lastScrollTop = 0
//cached scroll container of the active page (resolved lazily from ion-content);
//needed to measure distance from the bottom, which the event doesn't expose
let scrollEl = null
//the <ion-content> host whose (light-dom) children hold the scrollable content;
//we watch those children for size changes (see observeContentSize)
let contentEl = null
//the .topbars element (registered by App.vue); its height is the space hiding
//the bars frees up, which we need to predict whether a hide would trap the user
let topbarEl = null
//watches the page content so we can reveal the bars when it stops overflowing
let resizeObserver = null
//window 'resize' listener is attached once and lives for the app's lifetime
let windowResizeBound = false

//while the content rubber-bands at the very bottom, scrollTop overshoots and
//snaps back, producing fake "scrolling up" deltas. Reacting to them toggles the
//bars, which changes layout height and feeds back into more scroll events — a
//jittery loop. Freeze the state within this margin of the bottom.
const BOTTOM_FREEZE_MARGIN = 60

//if the content overflows by no more than this, treat it as "not really
//scrollable": there's no room (or only room inside the frozen bottom zone) to
//scroll up and bring the bars back, so they must be revealed automatically.
const UNSCROLLABLE_MARGIN = BOTTOM_FREEZE_MARGIN

function handleScroll(event) {
    //lazily cache the real scroll container (Ionic exposes it asynchronously)
    let content = event.target
    if (!scrollEl && content && content.getScrollElement) {
        contentEl = content
        content.getScrollElement().then((el) => {
            scrollEl = el
            observeContentSize()
        })
    }

    let scrollTop = event.detail.scrollTop
    let delta = scrollTop - lastScrollTop
    lastScrollTop = scrollTop

    //ignore everything inside the bottom overscroll/bounce zone
    if (scrollEl) {
        let distanceFromBottom = scrollEl.scrollHeight - scrollEl.clientHeight - scrollTop
        if (distanceFromBottom < BOTTOM_FREEZE_MARGIN) {
            return
        }
    }

    //always reveal when at/near the very top
    if (scrollTop <= 0) {
        state.topbarsHidden = false
        return
    }

    //ignore tiny jitter so the bars don't flicker
    if (Math.abs(delta) < 6) {
        return
    }

    if (delta > 0 && scrollTop > 40) {
        //scrolling down: hide the bars — unless hiding would shrink the content
        //below the scrollable threshold and strand the user with no way back up
        if (!state.topbarsHidden && !hidingWouldTrap()) {
            state.topbarsHidden = true
        }
    } else if (delta < 0) {
        //scrolling up
        state.topbarsHidden = false
    }
}

//Hiding the bars frees their height for the content. On a borderline range of
//screen sizes that freed space is exactly enough to make the content fit, so
//the scrollbar disappears the instant the bars slide away — and with nothing
//left to scroll there's no way to bring them back. Predict that here from the
//current (bars-visible) measurements and keep the bars put instead.
function hidingWouldTrap() {
    if (!scrollEl) {
        //can't measure yet; let the resize watchers recover if it does trap
        return false
    }
    let topbarHeight = topbarEl ? topbarEl.offsetHeight : 0
    let overflowAfterHide = (scrollEl.scrollHeight - scrollEl.clientHeight) - topbarHeight
    return overflowAfterHide <= UNSCROLLABLE_MARGIN
}

//When the scrollable content shrinks (e.g. a combined product collapses again
//after being added to the cart) or the window grows, the page can stop being
//scrollable while the bars are still hidden — leaving the user stuck with no way
//to scroll up and bring them back. Whenever that happens, reveal them.
function syncTopbarVisibility() {
    if (!state.topbarsHidden || !scrollEl) {
        return
    }
    let overflow = scrollEl.scrollHeight - scrollEl.clientHeight
    if (overflow <= UNSCROLLABLE_MARGIN) {
        state.topbarsHidden = false
        //the scroll position is now clamped near the (new) top; resync so the
        //next scroll delta is measured from there instead of the stale value
        lastScrollTop = scrollEl.scrollTop
    }
}

//watch both the page content and the window so a size change that removes the
//scrollbars also brings the topbars back. The scroll element itself never
//resizes when its content shrinks (its box stays the viewport size), so we
//observe the content children — their height is what the container measures.
function observeContentSize() {
    if (typeof ResizeObserver !== "undefined" && contentEl) {
        if (!resizeObserver) {
            resizeObserver = new ResizeObserver(() => { syncTopbarVisibility() })
        }
        resizeObserver.disconnect()
        for (let child of contentEl.children) {
            resizeObserver.observe(child)
        }
        //also watch the scroll container itself: it doesn't resize when its
        //content shrinks, but it does when the bars collapse or the window
        //changes — a safety net for the trap hidingWouldTrap() can't foresee
        if (scrollEl) {
            resizeObserver.observe(scrollEl)
        }
    }
    if (!windowResizeBound && typeof window !== "undefined") {
        window.addEventListener("resize", syncTopbarVisibility)
        windowResizeBound = true
    }
}

//App.vue registers its .topbars element so hidingWouldTrap() can read the
//height that collapsing the bars would free up
function setTopbarElement(el) {
    topbarEl = el
}

//a fresh view starts scrolled to the top with its own scroll container
function reset() {
    state.topbarsHidden = false
    lastScrollTop = 0
    scrollEl = null
    contentEl = null
    //stop watching the previous page's content; the next page re-observes once
    //its scroll container resolves. The window listener stays bound for reuse.
    if (resizeObserver) {
        resizeObserver.disconnect()
    }
}

export default {
    ...toRefs(state),
    handleScroll,
    setTopbarElement,
    reset
}
